import { Injectable, computed, signal } from '@angular/core';
import { Beneficiario } from '@app/models/masive-transfer.model';
import {
  RegistroMasivo,
  dejaAlMenosUno,
  numerarRegistros,
  quitarRegistros,
  totalEnCentavos,
} from '@app/domain/models/stp/registros-masivos';

interface DraftPayload {
  fileName: string;
  beneficiarios: Beneficiario[];
  /**
   * Se IGNORA: el total se deriva de los registros (SPGRA-528). Queda opcional
   * para no romper a quien todavía lo manda.
   */
  totalAmount?: number;
}

/** Lo único que se usa de `crypto`; parcial porque en un contexto no seguro falta
 *  `randomUUID` (y en un entorno sin Web Crypto, todo). */
export type FuenteAleatoria = Partial<Pick<Crypto, 'randomUUID' | 'getRandomValues'>>;

let generacionesSinCrypto = 0;

/**
 * Id de GENERACIÓN del borrador. Solo tiene que ser distinto en cada generación dentro
 * de la misma pestaña; no es secreto ni viaja al back.
 *
 * `crypto.randomUUID()` solo existe en contextos SEGUROS (https o localhost): servida
 * por http desde una IP de la red, el store (de raíz) reventaba al crearse y con él toda
 * la masiva. Por eso la cascada: `randomUUID` → `getRandomValues` (sí existe fuera de
 * contexto seguro) → contador monotónico + marca de tiempo.
 */
export function nuevaGeneracionDeBorrador(fuente: FuenteAleatoria | undefined = globalThis.crypto): string {
  if (typeof fuente?.randomUUID === 'function') {
    return fuente.randomUUID();
  }
  if (typeof fuente?.getRandomValues === 'function') {
    const bytes = fuente.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  generacionesSinCrypto += 1;
  return `gen-${Date.now().toString(36)}-${generacionesSinCrypto.toString(36)}`;
}

/**
 * Store en memoria del borrador de dispersión masiva (pantalla
 * /transactions/create-masive-transfer). Sobrevive a la salida/entrada
 * del componente para que la previsualización no obligue a re-subir el
 * archivo. Nunca se persiste a storage: si el usuario recarga la página,
 * el borrador se pierde intencionalmente (evita quedarse con datos
 * financieros fuera de sesión).
 *
 * Es la FUENTE ÚNICA del borrador: la tabla, los contadores, el total y lo que
 * se envía al confirmar salen de aquí. La copia cifrada que abre la revisión
 * previa es solo una vista filtrada por los ids que siguen vivos.
 *
 * ⚠️ Los ids de los registros son POSICIONALES (1..n) y se reutilizan en cada
 * `setDraft`, así que un id solo no dice de QUÉ borrador es. `borradorId` es la
 * GENERACIÓN: se renueva con cada borrador nuevo (y al limpiar), viaja en la copia
 * y la revisión previa descarta una copia de otra generación (SPGRA-528, R13).
 */
@Injectable({ providedIn: 'root' })
export class MasiveTransferDraftStore {
  private readonly _fileName = signal<string>('');
  private readonly _registros = signal<RegistroMasivo<Beneficiario>[]>([]);
  private readonly _observations = signal<string>('');
  private readonly _borradorId = signal<string>(nuevaGeneracionDeBorrador());

  readonly fileName = this._fileName.asReadonly();
  readonly registros = this._registros.asReadonly();
  readonly beneficiarios = computed(() => this._registros().map(registro => registro.datos));
  /** Derivado en centavos enteros: sin deriva de flotantes (R10). */
  readonly totalAmount = computed(() => totalEnCentavos(this._registros(), datos => datos.monto) / 100);
  readonly observations = this._observations.asReadonly();
  /** Generación del borrador; cambia en `setDraft` y `clear`, nunca al quitar registros. */
  readonly borradorId = this._borradorId.asReadonly();

  readonly hasDraft = computed(() => this._registros().length > 0);

  setDraft(payload: DraftPayload): void {
    this._borradorId.set(nuevaGeneracionDeBorrador());
    this._fileName.set(payload.fileName);
    this._registros.set(numerarRegistros(payload.beneficiarios));
    // Un archivo nuevo empieza sin observaciones (mismo comportamiento
    // que displayParsedData: setValue('') al cargar).
    this._observations.set('');
  }

  /**
   * Quita los registros indicados y devuelve cuántos quitó. Si la eliminación
   * dejaría el borrador vacío no toca nada y devuelve 0 (R12).
   */
  quitarRegistros(ids: readonly number[]): number {
    const actuales = this._registros();
    const aQuitar = new Set(ids);
    const quedan = quitarRegistros(actuales, aQuitar);
    const quitados = actuales.length - quedan.length;
    if (quitados === 0 || !dejaAlMenosUno(actuales.length, quitados)) {
      return 0;
    }
    this._registros.set(quedan);
    return quitados;
  }

  updateObservations(value: string): void {
    this._observations.set(value ?? '');
  }

  clear(): void {
    this._borradorId.set(nuevaGeneracionDeBorrador());
    this._fileName.set('');
    this._registros.set([]);
    this._observations.set('');
  }
}
